import { OgpApiService } from 'ogp/services/ogp-api-service';
import { Site } from 'shared/hooks';
import {
  ApplicationAverageReactionTimeResponse,
  applicationAverageReactionTimeSchema,
  ApplicationsPendingReviewResponse,
  applicationsPendingReviewSchema,
  CheckoutsAverageAcceptanceTimeResponse,
  checkoutsAverageAcceptanceTimeSchema,
  CheckoutsPendingReviewResponse,
  checkoutsPendingReviewSchema,
  DaysPlannedAheadResponse,
  daysPlannedAheadSchema,
  FillRateForFlexPoolWorkersResponse,
  fillRateForFlexPoolWorkersSchema,
  FillRateOfUpcomingShiftsResponse,
  fillRateOfUpcomingShiftsSchema,
  Interval,
  MedianShiftPublishingWindowResponse,
  medianShiftPublishingWindowSchema,
  NotPlannedWorkResponse,
  notPlannedWorkSchema,
  OpenCheckoutsResponse,
  openCheckoutsSchema,
  ShiftOverviewChartResponse,
  shiftOverviewChartResponseSchema,
  UnfulfilledShiftsResponse,
  unfulfilledShiftsSchema,
} from './dashboard-service.schema';
import { GetShiftOverviewDto, IntervalDto } from './dashboard-service.dto';

export class DashboardService {
  constructor(private readonly ogpApiService: OgpApiService) {}

  public async getOpenCheckouts(site: Site): Promise<OpenCheckoutsResponse> {
    const response = await this.getOpenCheckoutsImpl(site);
    return openCheckoutsSchema.parse(response);
  }

  private async getOpenCheckoutsImpl(site: Site): Promise<OpenCheckoutsResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<OpenCheckoutsResponse>({
        path: `todo/open-checkouts`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getUnfulfilledShifts(site: Site): Promise<UnfulfilledShiftsResponse> {
    const response = await this.getUnfulfilledShiftsImpl(site);
    return unfulfilledShiftsSchema.parse(response);
  }

  private async getUnfulfilledShiftsImpl(site: Site): Promise<UnfulfilledShiftsResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<UnfulfilledShiftsResponse>({
        path: `todo/unfulfilled-shifts`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getNotPlannedWork(site: Site): Promise<NotPlannedWorkResponse> {
    const response = await this.getNotPlannedWorkImpl(site);
    return notPlannedWorkSchema.parse(response);
  }

  private async getNotPlannedWorkImpl(site: Site): Promise<NotPlannedWorkResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<NotPlannedWorkResponse>({
        path: `todo/not-planned-work`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getShiftOverviewChart(
    site: Site,
    startDate: string,
    endDate: string,
    groupBy: Interval
  ): Promise<ShiftOverviewChartResponse> {
    const response = await this.getShiftOverviewChartImpl(site, startDate, endDate, groupBy);

    return shiftOverviewChartResponseSchema.parse(response);
  }
  public getShiftOverviewChartImpl = async (
    site: Site,
    startDate: string,
    endDate: string,
    groupBy: IntervalDto
  ): Promise<GetShiftOverviewDto> => {
    if (site === 'ogp') {
      return await this.ogpApiService.get<GetShiftOverviewDto>({
        path: `shift-overview`,
        params: {
          startDate,
          endDate,
          groupBy,
        },
      });
    }
    throw new Error('Not implemented');
  };

  public async getMedianShiftPublishingWindow(
    site: Site
  ): Promise<MedianShiftPublishingWindowResponse> {
    const response = await this.getMedianShiftPublishingWindowImpl(site);
    return medianShiftPublishingWindowSchema.parse(response);
  }

  private async getMedianShiftPublishingWindowImpl(
    site: Site
  ): Promise<MedianShiftPublishingWindowResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<MedianShiftPublishingWindowResponse>({
        path: `analytics/median-shift-publishing-window`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getApplicationAverageReactionTime(
    site: Site
  ): Promise<ApplicationAverageReactionTimeResponse> {
    const response = await this.getApplicationAverageReactionTimeImpl(site);
    return applicationAverageReactionTimeSchema.parse(response);
  }

  private async getApplicationAverageReactionTimeImpl(
    site: Site
  ): Promise<ApplicationAverageReactionTimeResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<ApplicationAverageReactionTimeResponse>({
        path: `analytics/application-average-reaction-time`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getApplicationsPendingReview(
    site: Site
  ): Promise<ApplicationsPendingReviewResponse> {
    const response = await this.getApplicationsPendingReviewImpl(site);
    return applicationsPendingReviewSchema.parse(response);
  }

  private async getApplicationsPendingReviewImpl(
    site: Site
  ): Promise<ApplicationsPendingReviewResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<ApplicationsPendingReviewResponse>({
        path: `analytics/applications-pending-review`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getCheckoutsAverageAcceptanceTime(
    site: Site
  ): Promise<CheckoutsAverageAcceptanceTimeResponse> {
    const response = await this.getCheckoutsAverageAcceptanceTimeImpl(site);
    return checkoutsAverageAcceptanceTimeSchema.parse(response);
  }

  private async getCheckoutsAverageAcceptanceTimeImpl(
    site: Site
  ): Promise<CheckoutsAverageAcceptanceTimeResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<CheckoutsAverageAcceptanceTimeResponse>({
        path: `analytics/checkouts-average-acceptance-time`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getCheckoutsPendingReview(site: Site): Promise<CheckoutsPendingReviewResponse> {
    const response = await this.getCheckoutsPendingReviewImpl(site);
    return checkoutsPendingReviewSchema.parse(response);
  }

  private async getCheckoutsPendingReviewImpl(site: Site): Promise<CheckoutsPendingReviewResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<CheckoutsPendingReviewResponse>({
        path: `analytics/checkouts-pending-review`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getDaysPlannedAhead(site: Site): Promise<DaysPlannedAheadResponse> {
    const response = await this.getDaysPlannedAheadImpl(site);
    return daysPlannedAheadSchema.parse(response);
  }

  private async getDaysPlannedAheadImpl(site: Site): Promise<DaysPlannedAheadResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<DaysPlannedAheadResponse>({
        path: `analytics/days-planned-ahead`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getFillRateForFlexPoolWorkers(
    site: Site
  ): Promise<FillRateForFlexPoolWorkersResponse> {
    const response = await this.getFillRateForFlexPoolWorkersImpl(site);
    return fillRateForFlexPoolWorkersSchema.parse(response);
  }

  private async getFillRateForFlexPoolWorkersImpl(
    site: Site
  ): Promise<FillRateForFlexPoolWorkersResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<FillRateForFlexPoolWorkersResponse>({
        path: `analytics/fill-rate-for-flex-pool-workers`,
      });
    }
    throw new Error('Not implemented');
  }

  public async getFillRateOfUpcomingShifts(site: Site): Promise<FillRateOfUpcomingShiftsResponse> {
    const response = await this.getFillRateOfUpcomingShiftsImpl(site);
    return fillRateOfUpcomingShiftsSchema.parse(response);
  }

  private async getFillRateOfUpcomingShiftsImpl(
    site: Site
  ): Promise<FillRateOfUpcomingShiftsResponse> {
    if (site === 'ogp') {
      return await this.ogpApiService.get<FillRateOfUpcomingShiftsResponse>({
        path: `analytics/fill-rate-of-upcoming-shifts`,
      });
    }
    throw new Error('Not implemented');
  }
}
